import { useLocation, useNavigate } from 'react-router-dom'
import { Search, ChevronRight, Heart } from 'lucide-react'
import { carePlans } from '../data/carePlans'
import { specialties } from '../data/specialties'
import { useScrollToTop } from '../hooks/useScrollToTop'
import { useScrollBehavior } from '../hooks/useScrollBehavior'
import Header from '../components/Header'
import Footer from '../components/Footer'
import FloatingSearchBox from '../components/FloatingSearchBox'

const SearchResults = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const query = searchParams.get('q') || ''

  // Scroll to top on page load
  useScrollToTop()

  // Scroll behavior for mobile
  const { isHeroVisible, showFloatingSearch } = useScrollBehavior()

  // Search through care plans and specialties
  const searchResults = []

  if (query.trim()) {
    const lowercaseQuery = query.toLowerCase()

    // Search care plans
    const matchingCarePlans = carePlans.filter(plan =>
      plan.title.toLowerCase().includes(lowercaseQuery) ||
      plan.description.toLowerCase().includes(lowercaseQuery) ||
      plan.category.toLowerCase().includes(lowercaseQuery)
    ).map(plan => ({ ...plan, type: 'care-plan' }))

    // Search specialties
    const matchingSpecialties = specialties.filter(specialty =>
      specialty.name.toLowerCase().includes(lowercaseQuery) ||
      specialty.description.toLowerCase().includes(lowercaseQuery)
    ).map(specialty => ({ ...specialty, type: 'specialty' }))

    // Combine and sort alphabetically
    searchResults.push(...matchingCarePlans, ...matchingSpecialties)
    searchResults.sort((a, b) => {
      const nameA = a.type === 'care-plan' ? a.title : a.name
      const nameB = b.type === 'care-plan' ? b.title : b.name
      return nameA.localeCompare(nameB)
    })
  }

  const handleResultClick = (result) => {
    if (result.type === 'care-plan') {
      navigate(`/care-plan/${result.id}`)
    } else {
      navigate(`/specialty/${result.id}`)
    }
  }

  return (
    <div className="App">
      <Header />

      <section className={`hero hero-section ${!isHeroVisible ? 'hidden' : ''}`}>
        <div className="container">
          <h1 style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <Search size={40} />
            Search Results
          </h1>
          <p>
            {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for: "{query}"
          </p>
        </div>
      </section>

      <div className="container">
        <div className="content">
          {query.trim() ? (
            searchResults.length > 0 ? (
              <div className="specialty-grid">
                {searchResults.map((result) => (
                  <div
                    key={`${result.type}-${result.id}`}
                    className="specialty-card"
                    onClick={() => handleResultClick(result)}
                  >
                    <h3>
                      {result.type === 'specialty' ? (
                        <result.icon size={20} />
                      ) : (
                        <Heart size={20} />
                      )}
                      {result.type === 'care-plan' ? result.title : result.name}
                    </h3>
                    <p>{result.description}</p>
                    <div style={{
                      position: 'absolute',
                      top: '1rem',
                      right: '3rem',
                      fontSize: '0.75rem',
                      color: '#00bcd4',
                      background: '#e0f7fa',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '0.25rem',
                      fontWeight: '500'
                    }}>
                      {result.type === 'care-plan' ? 'Care Plan' : 'Specialty'}
                    </div>
                    <ChevronRight className="chevron" size={16} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="favorites-empty">
                <Search size={48} />
                <h3>No results found</h3>
                <p>Try searching with different keywords or browse our specialties from the home page.</p>
              </div>
            )
          ) : (
            <div className="favorites-empty">
              <Search size={48} />
              <h3>Enter a search term</h3>
              <p>Search for care plans, conditions, or specialties to get started.</p>
            </div>
          )}
        </div>
      </div>

      {/* Floating Search Box for Mobile */}
      <FloatingSearchBox isVisible={showFloatingSearch} />

      <Footer />
    </div>
  )
}

export default SearchResults
